import { Target, Eye, Award, Users } from 'lucide-react';
import SEO from '../components/SEO';
import { Helmet } from 'react-helmet-async';

export default function AboutPage() {
  const values = [
    {
      icon: <Award size={40} />,
      title: 'Excellence',
      description: 'We strive for excellence in every service we deliver, ensuring the highest quality standards.',
    },
    {
      icon: <Users size={40} />,
      title: 'Client-Centric',
      description: 'Our clients success is our success. We prioritize understanding and meeting their unique needs.',
    },
    {
      icon: <Target size={40} />,
      title: 'Innovation',
      description: 'We embrace cutting-edge technologies and innovative approaches to solve complex challenges.',
    },
    {
      icon: <Eye size={40} />,
      title: 'Integrity',
      description: 'We operate with transparency, honesty, and ethical practices in all our business dealings.',
    },
  ];

  // Breadcrumb Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.skandaitsolutions.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "About",
        "item": "https://www.skandaitsolutions.com/about"
      }
    ]
  };

  return (
    <div data-testid="about-page" className="pt-20">
      <SEO 
        title="About Us - Leading IT Training Institute in Narasaraopeta"
        description="Learn about SKANDA IT SOLUTIONS - the most trusted IT training institute in Narasaraopeta, Andhra Pradesh. With experienced faculty, modern infrastructure, and 100% placement assistance, we have trained thousands of students since our establishment."
        keywords="about SKANDA IT SOLUTIONS, IT institute Narasaraopeta, computer training center, IT education, best IT training, experienced faculty, placement record, technology education"
        canonicalUrl="https://www.skandaitsolutions.com/about"
      />
      
      {/* Structured Data for better indexing */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-slate-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 data-testid="about-hero-title" className="text-5xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            About SKANDA IT SOLUTIONS
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Your trusted technology partner delivering comprehensive IT solutions for over a decade
          </p>
        </div>
      </section>

      {/* Company Overview */}
      <section className="section-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <img
              src="https://images.unsplash.com/photo-1680691257251-5fead813b73e?auto=format&fit=crop&q=80"
              alt="IT Solutions"
              className="rounded-lg shadow-2xl"
            />
          </div>
          <div>
            <h2 data-testid="company-overview-title" className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Who We Are
            </h2>
            <div className="space-y-4 text-lg text-slate-600 leading-relaxed">
              <p>
                SKANDA IT SOLUTIONS is a leading IT services company specializing in providing end-to-end technology solutions for individuals, corporates, and enterprises. With over 10 years of industry experience, we have established ourselves as a trusted partner in the IT ecosystem.
              </p>
              <p>
                Our comprehensive service portfolio includes professional IT training programs, placement assistance, computer and laptop sales, hardware installation, networking solutions, and complete email setup and configuration services.
              </p>
              <p>
                We pride ourselves on our commitment to excellence, delivering innovative solutions that drive business growth and empower individuals to achieve their career aspirations in the technology sector.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div data-testid="mission-section" className="bg-white p-10 rounded-lg shadow-lg">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <Target size={32} className="text-blue-600" />
              </div>
              <h3 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Our Mission
              </h3>
              <p className="text-slate-600 text-lg leading-relaxed">
                To empower individuals with industry-relevant IT skills and provide enterprises with cutting-edge technology solutions that drive digital transformation and operational excellence.
              </p>
            </div>
            <div data-testid="vision-section" className="bg-white p-10 rounded-lg shadow-lg">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mb-6">
                <Eye size={32} className="text-sky-600" />
              </div>
              <h3 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Our Vision
              </h3>
              <p className="text-slate-600 text-lg leading-relaxed">
                To be recognized as the premier IT services provider, known for delivering exceptional training programs, achieving outstanding placement records, and providing comprehensive technology solutions across India.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="section-container">
        <div className="text-center mb-16">
          <h2 data-testid="values-heading" className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Our Core Values
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            The principles that guide our work and define our culture
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {values.map((value, index) => (
            <div key={index} data-testid={`value-card-${index}`} className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-sky-500 rounded-full flex items-center justify-center mx-auto mb-6 text-white">
                {value.icon}
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {value.title}
              </h3>
              <p className="text-slate-600">
                {value.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-gradient-to-r from-blue-600 to-sky-500 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 data-testid="expertise-heading" className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Our Expertise
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div data-testid="expertise-training" className="text-center">
              <div className="text-5xl font-bold mb-2">500+</div>
              <div className="text-blue-100 text-lg">Students Successfully Trained</div>
            </div>
            <div data-testid="expertise-placements" className="text-center">
              <div className="text-5xl font-bold mb-2">95%</div>
              <div className="text-blue-100 text-lg">Placement Success Rate</div>
            </div>
            <div data-testid="expertise-clients" className="text-center">
              <div className="text-5xl font-bold mb-2">200+</div>
              <div className="text-blue-100 text-lg">Corporate Clients Served</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}